import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Plus, Minus, ArrowRight } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { SERVICES } from "@/content/services";
import { ENGINE_LANDING_LIST } from "@/content/engineLanding";
import { ENGINES } from "@/content/engines";
import { getIcon } from "@/components/iconMap";
import Seo from "@/components/Seo";

export default function ServicesPage() {
  const [openId, setOpenId] = useState(null);

  return (
    <div data-testid="services-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <Seo title="Game Development Services — Hire Engine Specialists" description="Game development services from an Australian studio: Unreal Engine development, Unity, Arma Reforger (Enfusion), FiveM, Minecraft, GTA V — plus a fully open modding market for Rust, Garry's Mod and any custom game. Quotes within 24 hours." path="/services" keywords="game development services, unreal engine development services, hire game developers, game outsourcing studio, UE5 services, unity development services, arma reforger developer, fivem developer, rust modding, garry's mod developer, custom game mods" />
      <SiteNav />

      <header className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <p className="font-mono text-xs tracking-[0.4em] text-nc-blue uppercase mb-4">Outsourcing Program</p>
          <h1 data-testid="services-title" className="font-sans font-bold uppercase tracking-tight text-3xl sm:text-5xl text-zinc-50">
            Services
          </h1>
          <p className="font-mono text-sm text-zinc-400 mt-4 max-w-2xl leading-relaxed">
            Every discipline of game development under one roof — delivered across Unreal Engine 5, Unity and CryEngine.
            50% upfront, 50% on delivery. Quotes back within 24 hours.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {["UNREAL ENGINE 5", "UNITY", "ARMA REFORGER", "FIVEM (GTA V)", "MINECRAFT", "CRYENGINE"].map((e) => (
              <span key={e} className="font-mono text-[10px] tracking-[0.25em] text-nc-orange border border-nc-orange/40 bg-nc-orange/10 px-3 py-1.5">{e}</span>
            ))}
          </div>
        </div>
      </header>

      <section data-testid="services-engine-section" className="border-b border-white/10 bg-black/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
          <p className="font-mono text-xs tracking-[0.4em] text-nc-blue uppercase mb-3">Choose Your Platform</p>
          <h2 data-testid="services-engine-title" className="font-sans font-bold uppercase tracking-tight text-2xl sm:text-3xl text-zinc-50">
            Services by Engine
          </h2>
          <p className="font-mono text-sm text-zinc-400 mt-3 max-w-2xl leading-relaxed">
            Every platform has its own dedicated service page — offerings, FAQs and instant quote access.{" "}
            <span className="text-nc-orange">Click a card to explore.</span>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            {ENGINE_LANDING_LIST.map((p) => {
              const meta = ENGINES[p.engine] || p.engineMeta;
              const Icon = meta.icon;
              return (
                <Link
                  key={p.slug}
                  to={`/${p.slug}`}
                  data-testid={`services-engine-card-${p.engine}`}
                  className="group relative border border-white/15 bg-black/40 p-6 cursor-pointer transition-all duration-300 hover:border-nc-blue hover:bg-nc-blue/5 hover:-translate-y-1 hover:shadow-[0_10px_40px_-10px_rgba(46,107,255,0.4)]"
                >
                  <div className="flex items-center justify-between">
                    <span className={`w-11 h-11 border flex items-center justify-center ${meta.accent}`}>
                      <Icon className="w-5 h-5" strokeWidth={1.75} />
                    </span>
                    <ArrowRight className="w-5 h-5 text-zinc-600 transition-all duration-300 group-hover:text-nc-blue group-hover:translate-x-1.5" />
                  </div>
                  <p className="font-sans font-bold uppercase text-lg text-zinc-50 mt-5 transition-colors duration-300 group-hover:text-nc-blue">
                    {meta.label} <span className="text-nc-orange">Services</span>
                  </p>
                  <p className="font-mono text-[11px] text-zinc-500 mt-2 leading-relaxed">{p.cardBlurb}</p>
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-nc-blue mt-5 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    Explore services →
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-14 w-full flex flex-col gap-3">
        {SERVICES.map((s, i) => {
          const Icon = getIcon(s.icon);
          const open = openId === s.id;
          return (
            <motion.div
              key={s.id}
              data-testid={`service-${s.id}`}
              className={`border transition-colors duration-300 ${open ? "border-nc-blue/60 bg-nc-blue/5" : "border-white/10 bg-black/40 hover:border-white/30"}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
            >
              <button
                data-testid={`service-${s.id}-toggle`}
                onClick={() => setOpenId(open ? null : s.id)}
                className="w-full flex items-center gap-4 px-5 py-4 text-left"
              >
                <Icon className={`w-5 h-5 shrink-0 ${open ? "text-nc-blue" : "text-zinc-500"}`} strokeWidth={1.75} />
                <span className="font-sans font-bold uppercase text-lg text-zinc-100 flex-1">{s.title}</span>
                {s.engines.map((e) => (
                  <span key={e} className="hidden sm:block font-mono text-[9px] tracking-[0.2em] text-zinc-500 border border-white/10 px-2 py-1">{e}</span>
                ))}
                {open ? <Minus className="w-4 h-4 text-nc-blue shrink-0" /> : <Plus className="w-4 h-4 text-zinc-500 shrink-0" />}
              </button>
              <AnimatePresence>
                {open && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5 pl-14">
                      <p className="font-mono text-xs text-zinc-400 leading-relaxed max-w-2xl">{s.description}</p>
                      <Link to="/contact" className="font-mono text-[10px] tracking-[0.25em] text-nc-orange uppercase mt-4 inline-flex items-center gap-2 hover:underline">
                        Request this service <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}

        <div className="mt-10 border border-white/10 bg-black/40 p-8 flex flex-col md:flex-row md:items-center gap-6">
          <p className="font-mono text-xs text-zinc-400 leading-relaxed flex-1">
            All work is covered under our EULA and mutual NDA — your project stays confidential from first contact.
          </p>
          <div className="flex gap-3 shrink-0">
            <Link to="/eula" className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-300 border border-white/15 hover:border-nc-blue hover:text-nc-blue px-4 py-2.5 transition-colors duration-300">EULA</Link>
            <Link to="/nda" className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-300 border border-white/15 hover:border-nc-blue hover:text-nc-blue px-4 py-2.5 transition-colors duration-300">NDA</Link>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
